import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { CheckCircle2, Loader2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { formatMoney } from '../lib/money';

/**
 * ConfirmPaymentButton
 * Renders a small "Confirm ___ payment" button; opens a dialog that lets the
 * caller pick the payment method + reference (e.g. e-transfer confirmation
 * number). On submit, calls the provided `onConfirm({ method, reference })`
 * and shows a toast.
 *
 * Props:
 *   - kind: 'goods' | 'platform' — controls label + method options
 *   - orderId
 *   - amount, currency (display-only)
 *   - status: current invoice status ('pending' | 'paid' | 'not_required')
 *   - onConfirm: async ({ method, reference }) => any
 *   - buttonSize, buttonVariant (optional)
 */
const METHOD_OPTIONS = {
  goods: [
    { value: 'card', label: 'Card' },
    { value: 'etransfer', label: 'E-transfer' },
    { value: 'cash', label: 'Cash' },
  ],
  platform: [
    { value: 'card', label: 'Card' },
    { value: 'wallet', label: 'ShipitNow wallet' },
    { value: 'etransfer', label: 'E-transfer' },
    { value: 'giftcard', label: 'ShipitNow gift card' },
  ],
};

const LABELS = {
  goods: {
    button: 'Confirm goods payment',
    title: 'Confirm goods payment received',
    subtitle: 'This releases the vendor invoice. The order still needs ShipitNow admin confirmation for the delivery + service fees before a driver is assigned.',
  },
  platform: {
    button: 'Confirm ShipitNow payment',
    title: 'Confirm ShipitNow payment received',
    subtitle: 'This releases the delivery + service invoice. Combined with the vendor\'s goods-payment confirmation, the order becomes visible to drivers.',
  },
};

export const ConfirmPaymentButton = ({
  kind = 'goods',
  orderId,
  amount,
  currency = 'CAD',
  status = 'pending',
  onConfirm,
  buttonSize = 'sm',
  buttonVariant = 'default',
}) => {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState('card');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);

  if (status === 'paid') {
    return (
      <span
        data-testid={`${kind}-invoice-paid-${orderId}`}
        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700"
      >
        <CheckCircle2 className="h-3.5 w-3.5" /> {kind === 'goods' ? 'Goods paid' : 'ShipitNow paid'}
      </span>
    );
  }
  if (status === 'not_required') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500">
        No goods invoice
      </span>
    );
  }

  const labels = LABELS[kind];
  const options = METHOD_OPTIONS[kind];

  const doConfirm = async () => {
    if (!method) return;
    setBusy(true);
    try {
      await onConfirm({ method, reference: reference.trim() });
      toast.success(`${kind === 'goods' ? 'Goods' : 'ShipitNow'} payment confirmed for ${orderId}`);
      setOpen(false);
      setReference('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to confirm ${kind} payment`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size={buttonSize}
        variant={buttonVariant}
        data-testid={`confirm-${kind}-payment-btn-${orderId}`}
        onClick={() => setOpen(true)}
      >
        <Wallet className="mr-1.5 h-3.5 w-3.5" />
        {labels.button}
      </Button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="sm:max-w-md" data-testid={`${kind}-confirm-dialog-${orderId}`}>
          <DialogHeader>
            <DialogTitle>{labels.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">{labels.subtitle}</p>
            {typeof amount === 'number' && (
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                <span className="text-slate-500">Invoice amount</span>
                <span className="font-semibold text-slate-900">
                  {formatMoney(amount, currency)}
                </span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor={`method-${orderId}`}>Payment method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger id={`method-${orderId}`} data-testid={`${kind}-method-select-${orderId}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`${kind}-method-${o.value}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor={`ref-${orderId}`}>Reference (optional)</Label>
              <Input
                id={`ref-${orderId}`}
                data-testid={`${kind}-reference-input-${orderId}`}
                placeholder="e.g. e-transfer code, receipt no."
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={doConfirm}
              disabled={busy}
              data-testid={`${kind}-confirm-submit-${orderId}`}
            >
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
              Mark as received
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConfirmPaymentButton;
